import { useCallback } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import {
	Message,
	useMessagePublisher,
} from '@confluence/page-tree-refresh-state-container/entry-points/messageBus.ts';

import SmartFolderSubTypeWriteFragmentArtifact from './__generated__/useApplySmartFolderSubTypeChangeContentWriteFragment.graphql.ts';
import type { useApplySmartFolderSubTypeChangeContentWriteFragment$data } from './__generated__/useApplySmartFolderSubTypeChangeContentWriteFragment.graphql.ts';

type ApplySmartFolderSubTypeChangeInput = {
	folderId: string;
	subType: 'managed' | null;
};

/**
 * Centralizes the local state propagation after a smart-folder sync or unsync
 * mutation succeeds:
 *
 * 1. Patches the normalized `Content:<folderId>` entity in the Apollo/Relay
 *    cache so every consumer reading `subType` (e.g. the folder view header,
 *    the page-tree quick actions) reflects the new state without an extra
 *    refetch.
 * 2. Publishes a `SmartFolderSyncStateChanged` message so non-GraphQL
 *    consumers — most importantly the page tree's sweet-state store — can
 *    update their own state (flip the icon, invalidate the folder's children).
 *
 * Call this from any sync/unsync mutation hook on the success path.
 */
export const useApplySmartFolderSubTypeChange = (): (({
	folderId,
	subType,
}: ApplySmartFolderSubTypeChangeInput) => void) => {
	const writeContentSubType = useWriteFragmentInterop<
		Omit<useApplySmartFolderSubTypeChangeContentWriteFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: SmartFolderSubTypeWriteFragmentArtifact,
		fragmentSpec: graphql`
			fragment useApplySmartFolderSubTypeChangeContentWriteFragment on Content {
				id
				subType
			}
		`,
	});

	const publishSyncStateChange = useMessagePublisher(Message.SmartFolderSyncStateChanged);

	return useCallback(
		({ folderId, subType }: ApplySmartFolderSubTypeChangeInput) => {
			writeContentSubType({
				fragmentId: `Content:${folderId}`,
				data: {
					id: folderId,
					subType,
					__typename: 'Content',
				},
			});
			publishSyncStateChange({ contentId: folderId, subType });
		},
		[writeContentSubType, publishSyncStateChange],
	);
};
